import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { markPointerShown, completePointer } from "@/api/featureSpotlights";
import { spotlightTarget } from "@touchworks/shared/platform/spotlightTargets.js";
import { useCandidates } from "./useCandidates";
import { OPEN_MODAL_SELECTOR, useDomPresence } from "./useDomPresence";
import { useSpotlightGate } from "./useSpotlightGate";
import { attributeSelector, useVisibleElement } from "./useVisibleElement";
import { useVisibleStep } from "./useVisibleStep";

/** O atributo que marca, no código da tela, o elemento de um recurso registrado. */
export const SPOTLIGHT_TARGET_ATTRIBUTE = "data-spotlight-target";

/** Sem regra de plano/integração do chamador: todo recurso conhecido vale. */
const allowEveryTarget = () => true;

/**
 * O ponteiro: a segunda parada de uma novidade com recurso dentro da tela (SPOT-14 a 18).
 *
 * Depois que a pessoa clica no botão do cartão, o servidor devolve a novidade em `pointers`
 * (armada). Este hook escolhe o primeiro ponteiro que ela pode ver, espera o elemento do recurso
 * aparecer na tela (uma conversa aberta, outra aba), e só então o mostra.
 *
 * Não aparece se: o recurso é desconhecido deste cliente; o item de menu dono do recurso não está
 * no menu da pessoa (nível, parceiro, programa); ou ela não tem o recurso do plano/integração.
 * Espera o que bloqueia a tela e some enquanto um diálogo está aberto, a não ser que o elemento
 * apontado esteja dentro desse diálogo.
 *
 * Um recurso que só existe depois de uma ação (uma conversa aberta, uma aba) declara `steps` no
 * registro: enquanto o recurso não está na tela, o anel aponta o passo mais adiantado que está
 * ("Abra uma conversa na lista") e passa ao recurso quando ele aparece. Sem isso a pessoa chegaria
 * numa tela sem nada marcado.
 *
 * Desfecho: clicar no próprio elemento grava `used` (o clique segue para o elemento, nada é
 * interceptado); "Entendi" grava `acknowledged`. O primeiro vence, no servidor. Os passos não
 * gravam desfecho: só o recurso, ou "Entendi".
 *
 * @param {{
 *   visibleNavKeys: string[],
 *   canSeeTarget?: (target: object) => boolean,
 *   tourClientId?: string|null,
 *   enabled?: boolean,
 * }} params
 */
export function useSpotlightPointer({ visibleNavKeys, canSeeTarget = allowEveryTarget, tourClientId = null, enabled = true }) {
  const { data } = useCandidates(enabled);
  // Sem ponteiro armado não há o que esperar nem o que esconder: nenhum observador de DOM.
  const hasPointers = enabled && (data?.pointers?.length ?? 0) > 0;
  const gated = useSpotlightGate({ tourClientId, enabled: hasPointers });
  const modalOpen = useDomPresence(OPEN_MODAL_SELECTOR, hasPointers);
  const [doneIds, setDoneIds] = useState(() => new Set());
  const shownIds = useRef(new Set());
  const finishing = useRef(new Set());

  const keysSignature = visibleNavKeys.join("|");
  const pointers = data?.pointers;
  const pointer = useMemo(() => {
    if (!enabled) return null;
    const keys = keysSignature.split("|");
    return (
      (pointers ?? []).find((candidate) => {
        if (doneIds.has(candidate.id)) return false;
        const target = spotlightTarget(candidate.targetKey);
        return Boolean(target) && keys.includes(target.navKey) && canSeeTarget(target);
      }) ?? null
    );
    // `canSeeTarget` é refeito pelo layout quando o plano carrega ou muda; o resultado é o mesmo
    // objeto da lista (a identidade do ponteiro escolhido não muda), então refazer a cada render é barato.
  }, [enabled, pointers, keysSignature, doneIds, canSeeTarget]);

  const selector = pointer && !gated ? attributeSelector(SPOTLIGHT_TARGET_ATTRIBUTE, pointer.targetKey) : null;
  const targetElement = useVisibleElement(selector);

  // Os passos só valem enquanto o recurso não está na tela.
  const steps = pointer ? (spotlightTarget(pointer.targetKey)?.steps ?? []) : [];
  const reached = useVisibleStep(selector && !targetElement ? steps.map((step) => attributeSelector(SPOTLIGHT_TARGET_ATTRIBUTE, step.key)) : []);
  // O estado do passo alcançado só se renova depois do efeito: num render em que o ponteiro acabou
  // de sair (um "Entendi"), `reached` ainda guarda um índice que a lista de passos já não tem.
  const reachedStep = reached ? steps[reached.index] : undefined;
  const guide = !targetElement && reachedStep ? { index: reached.index, total: steps.length + 1, text: reachedStep.text } : null;

  const element = targetElement ?? (reachedStep ? reached.element : null);
  // Um diálogo aberto esconde o ponteiro, a menos que o próprio elemento esteja dentro dele (o
  // detalhe de um negócio, uma conversa aberta numa gaveta): aí o anel e o cartão vão por cima.
  const insideModal = Boolean(element?.closest?.(OPEN_MODAL_SELECTOR));
  const visible = Boolean(pointer) && Boolean(element) && !gated && (!modalOpen || insideModal);
  const stepLabel = steps.length === 0 ? null : guide ? `Passo ${guide.index + 1} de ${guide.total}` : `Passo ${steps.length + 1} de ${steps.length + 1}`;

  useEffect(() => {
    if (!visible || shownIds.current.has(pointer.id)) return;
    shownIds.current.add(pointer.id);
    markPointerShown(pointer.id).catch(() => {});
  }, [visible, pointer]);

  const finish = useCallback(
    (outcome) => {
      if (!pointer || finishing.current.has(pointer.id)) return;
      finishing.current.add(pointer.id);
      setDoneIds((previous) => new Set(previous).add(pointer.id));
      completePointer(pointer.id, outcome).catch(() => {});
    },
    [pointer],
  );

  // Clicar no elemento do recurso é usá-lo. Escuta na captura e não interrompe nada: o clique
  // continua até o elemento, que faz o que sempre fez.
  useEffect(() => {
    if (!visible || !targetElement) return undefined;
    const selectorOfTarget = attributeSelector(SPOTLIGHT_TARGET_ATTRIBUTE, pointer.targetKey);
    const onClick = (event) => {
      if (event.target?.closest?.(selectorOfTarget)) finish("used");
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [visible, targetElement, pointer, finish]);

  const acknowledge = useCallback(() => finish("acknowledged"), [finish]);

  return { pointer, element, guide, stepLabel, insideModal, visible, acknowledge };
}
